import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Trophy, LogOut, UserCheck, Clock, XCircle, Home, ShieldCheck, Pencil, Check, Award, ShoppingBag, Dice5 } from 'lucide-react';
import { useAuth, formatApiError } from '../../context/AuthContext';
import LineupBuilder from './LineupBuilder';
import MarketStatusCard from './MarketStatusCard';
import PrizePoolCard from './PrizePoolCard';
import WalletButton from './WalletButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const map = {
    pending: { icon: Clock, cls: 'bg-amber-500/20 text-amber-300 border-amber-400/30', label: 'Aguarda aprovação' },
    approved: { icon: UserCheck, cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30', label: 'Aprovado' },
    rejected: { icon: XCircle, cls: 'bg-red-500/20 text-red-300 border-red-400/30', label: 'Rejeitado' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${s.cls}`}>
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
};

const TeamNameEditor = ({ team, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(team.team_name);
  const save = async () => {
    if (!name.trim() || name === team.team_name) { setEditing(false); return; }
    try {
      const { data } = await axios.put(`${API}/fantasy/my-team/name`, { team_name: name.trim() });
      onUpdate(data);
      setEditing(false);
    } catch (ex) { alert(formatApiError(ex)); }
  };
  return editing ? (
    <div className="flex items-center gap-2">
      <input
        autoFocus value={name} onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && save()}
        maxLength={40}
        className="bg-slate-800/70 border border-emerald-400/50 rounded-lg px-3 py-1.5 text-white text-sm w-56 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        data-testid="team-name-input"
      />
      <button onClick={save} className="p-1.5 rounded-lg bg-emerald-500 text-slate-900" data-testid="team-name-save">
        <Check className="w-4 h-4" />
      </button>
    </div>
  ) : (
    <div className="flex items-center gap-2">
      <div className="text-xl font-black text-white" data-testid="team-name-display">{team.team_name}</div>
      <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-emerald-300" title="Renomear equipa" data-testid="team-name-edit">
        <Pencil className="w-4 h-4" />
      </button>
    </div>
  );
};

const FantasyPage = () => {
  const { user, logout, refreshMe } = useAuth();
  const [profile, setProfile] = useState(null);
  const [team, setTeam] = useState(null);
  const [market, setMarket] = useState(null);
  const [lineup, setLineup] = useState(null);
  const [players, setPlayers] = useState([]);
  const [available, setAvailable] = useState([]);
  const [playerName, setPlayerName] = useState('');
  const [positionType, setPositionType] = useState('LINE');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [skippingClaim, setSkippingClaim] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadFantasyData = useCallback(async () => {
    try {
      const [mp, mkt] = await Promise.all([
        axios.get(`${API}/fantasy/my-profile`),
        axios.get(`${API}/fantasy/match-days/current`),
      ]);
      setProfile(mp.data);
      setMarket(mkt.data);

      // Fetch team + lineup + players only if fully authorized to play
      const canPlay = (mp.data && mp.data.status === 'approved') || user?.role === 'admin' || (user?.coach_only && user?.coach_approved);
      if (canPlay) {
        const [t, pl] = await Promise.all([
          axios.get(`${API}/fantasy/my-team`),
          axios.get(`${API}/fantasy/players`),
        ]);
        setTeam(t.data);
        setPlayers(pl.data);
        if (mkt.data.match_day) {
          const lu = await axios.get(`${API}/fantasy/my-lineup`, { params: { match_day_id: mkt.data.match_day.id } });
          setLineup(lu.data);
        }
      } else if (user && !user.coach_only && user.role !== 'admin') {
        // Legacy accounts that never went through coach mode — opt them in now.
        try { await axios.post(`${API}/fantasy/skip-claim`); await refreshMe(); } catch { /* noop */ }
      }
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally { setLoading(false); }
  }, [user, refreshMe]);

  useEffect(() => { loadFantasyData(); }, [loadFantasyData]);

  // Refresh market status every 60s so countdown/lock stays fresh
  useEffect(() => {
    const it = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/fantasy/match-days/current`);
        setMarket(data);
      } catch { /* noop */ }
    }, 60000);
    return () => clearInterval(it);
  }, []);

  const claim = async (e) => {
    e.preventDefault();
    setErr(''); setOk(''); setClaiming(true);
    try {
      await axios.post(`${API}/fantasy/claim-profile`, { player_name: playerName, position_type: positionType });
      setOk('Pedido enviado! Aguarda a aprovação do administrador.');
      await loadFantasyData();
      await refreshMe();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally { setClaiming(false); }
  };

  const skipClaim = async () => {
    setErr(''); setOk(''); setSkippingClaim(true);
    try {
      await axios.post(`${API}/fantasy/skip-claim`);
      await refreshMe();
      await loadFantasyData();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally { setSkippingClaim(false); }
  };

  const isApproved = profile?.status === 'approved' || user?.role === 'admin' || (user?.coach_only && user?.coach_approved);
  const isCoachPending = user?.coach_only && !user?.coach_approved && user?.role !== 'admin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900">
      <header className="border-b border-white/10 bg-slate-950/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 shrink-0">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 hidden sm:block">
              <div className="text-white font-black leading-tight tracking-tight text-sm sm:text-base truncate">RatingPro Fantasy</div>
              <div className="text-[10px] sm:text-xs text-emerald-300/80 italic leading-tight">O Manager Oficial da Nossa Pelada</div>
            </div>
          </Link>
          <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
            {user && <WalletButton balance={user.procoins_balance} />}
            <Link to="/" className="text-slate-300 hover:text-white text-sm inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-white/5" data-testid="fantasy-home-link" title="Início">
              <Home className="w-4 h-4" /> <span className="hidden sm:inline">Início</span>
            </Link>
            <Link to="/fantasy/mercado" className="text-emerald-300 hover:text-emerald-200 text-sm inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-emerald-500/10" data-testid="fantasy-market-link" title="Mercado">
              <ShoppingBag className="w-4 h-4" /> <span className="hidden sm:inline">Mercado</span>
            </Link>
            <Link to="/fantasy/apostas" className="text-fuchsia-300 hover:text-fuchsia-200 text-sm inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-fuchsia-500/10" data-testid="fantasy-bets-link" title="Apostas">
              <Dice5 className="w-4 h-4" /> <span className="hidden sm:inline">Apostas</span>
            </Link>
            <Link to="/fantasy/ranking" className="text-amber-300 hover:text-amber-200 text-sm inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-amber-500/10" data-testid="fantasy-ranking-link" title="Ranking">
              <Award className="w-4 h-4" /> <span className="hidden sm:inline">Ranking</span>
            </Link>
            {user?.role === 'admin' && (
              <Link to="/fantasy/admin" className="text-emerald-300 hover:text-emerald-200 text-sm inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-emerald-500/10" data-testid="fantasy-admin-link" title="Painel Admin">
                <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">Painel</span>
              </Link>
            )}
            <button onClick={logout} className="text-slate-300 hover:text-white text-sm inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-white/5" data-testid="fantasy-logout-btn" title="Sair">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-slate-300">A carregar...</div>
        ) : !isApproved ? (
          /* CLAIM FLOW (unchanged from Phase 1) */
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white">Olá, {user?.name} 👋</h1>
              <p className="text-emerald-200/70 mt-1">Bem-vindo ao RatingPro Fantasy</p>
            </div>
            <section className="bg-slate-900/60 backdrop-blur border border-white/10 rounded-2xl p-6 max-w-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Perfil de Jogador</h2>
                {profile && <StatusBadge status={profile.status} />}
              </div>
              {profile ? (
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between border-b border-white/5 pb-2">
                    <span className="text-slate-400">Jogador vinculado</span>
                    <span className="text-white font-medium">{profile.player_name}</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-white/5 pb-2">
                    <span className="text-slate-400">Posição</span>
                    <span className="text-white font-medium">{profile.position_type === 'GK' ? 'Guarda-Redes' : 'Jogador de Linha'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Preço no mercado</span>
                    <span className="text-emerald-300 font-bold">{profile.current_price?.toFixed(1)} €M</span>
                  </div>
                  {profile.status === 'pending' && (
                    <div className="text-amber-300/90 bg-amber-500/10 border border-amber-400/20 rounded-lg p-3 text-xs mt-3">
                      ⏳ O administrador ainda não aprovou este vínculo.
                    </div>
                  )}
                  {profile.status === 'rejected' && (
                    <div className="text-red-300/90 bg-red-500/10 border border-red-400/20 rounded-lg p-3 text-xs mt-3">
                      Este vínculo foi rejeitado. Fala com o administrador para corrigir.
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3 text-sm" data-testid="coach-pending-panel">
                  <div className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-400/30 rounded-lg">
                    <Clock className="w-5 h-5 text-amber-300 shrink-0" />
                    <div>
                      <div className="text-amber-200 font-semibold">A tua conta aguarda aprovação</div>
                      <div className="text-amber-100/70 text-xs mt-0.5">
                        O administrador vai confirmar o pagamento MB WAY e aprovar a tua conta de treinador. Assim que for libertada, podes escalar a tua equipa aqui.
                      </div>
                    </div>
                  </div>
                  {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
                </div>
              )}
            </section>
          </>
        ) : (
          /* APPROVED: LINEUP BUILDER */
          <>
            {/* Top row: greeting + team name */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-white">Olá, {user?.name} 👋</h1>
                <p className="text-emerald-200/70 mt-1">Monta a tua equipa da jornada</p>
              </div>
              {team && (
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">A tua Equipa</div>
                  <TeamNameEditor team={team} onUpdate={setTeam} />
                  <div className="text-xs text-slate-400 mt-1">
                    Mensalidade: {team.monthly_paid
                      ? <span className="text-emerald-400 font-bold">✓ Paga</span>
                      : <span className="text-amber-400 font-bold">Pendente</span>}
                  </div>
                </div>
              )}
            </div>

            {/* Monthly prize pool */}
            <div className="mb-6">
              <PrizePoolCard />
            </div>

            {/* Market status */}
            {market && (
              <div className="mb-6">
                <MarketStatusCard status={market} />
              </div>
            )}

            {/* Lineup builder */}
            {market?.match_day ? (
              <LineupBuilder
                players={players}
                matchDayId={market.match_day.id}
                initialLineup={lineup}
                marketOpen={market.market_open}
                onSaved={(saved) => setLineup(saved)}
              />
            ) : (
              <div className="bg-slate-900/40 border border-dashed border-white/10 rounded-2xl p-8 text-center">
                <Trophy className="w-10 h-10 text-emerald-400/60 mx-auto mb-2" />
                <h3 className="text-white font-semibold">Nenhuma jornada activa</h3>
                <p className="text-slate-400 text-sm mt-1">O administrador ainda não abriu a jornada. Volta em breve!</p>
              </div>
            )}

            {err && <div className="mt-6 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
          </>
        )}
      </main>
    </div>
  );
};

export default FantasyPage;
